/**
 * translations.js
 *
 * All UI translations for the SIBO breath test report.
 *
 * Usage:
 *   import { getTranslations } from './translations';
 *   const t = getTranslations('german'); // 'english' | 'french' | 'german'
 */

/**
 * Returns the full translation map for the requested language.
 * Falls back to English when the language is not recognised.
 *
 * @param {'english'|'french'|'german'} language
 * @returns {object}
 */
function getTranslations(language) {
  const translations = {
    english: {
      title: "SIBO BREATH TEST",
      generalDetails: "General Details",
      patientDetails: "Patient Details",
      testDetails: "Test Details",
      results: "Results",
      lastName: "Last name",
      firstName: "First name",
      dateOfBirth: "Date of birth",
      dateOfSampleCollection: "Date of sample collection",
      dateOfAnalysis: "Date of analysis",
      substratUsed: "Substrate used",
      reportSentTo: "Report sent to",
      orderCountry: "Order Country",
      voluntaryDetails: "Voluntary Details",
      physicianOrTherapist: "Physician or Therapist",
      occurenceOfSymptoms: "Occurence of Symptoms During Test",
      dailySymptoms: "Daily Symptoms",
      siboBreathTest: "SIBO** Breath Test",
      initialTest: "Initial Test",
      followupTest: "Follow-up Test",
      overview:
        "Overview of Hydrogen (H₂) and Methane (CH₄) values in parts per million (ppm)",
      summary: "Summary",
      gasRise: "Gas Rise",
      patientResults: "Gas Build-up",
      referenceValues: "Reference Values",
      continuePage2: "Continue to Page 2",
      importantRemarks: "Important Remarks",
      patient: "Patient",
      therapist: "Therapist",
      noSymptoms: "No symptoms were reported during the test.",
      symptomsReported:
        "The patient reports a first occurrence of the following symptoms during the test:",
      tooLow: "Too Low",
      normal: "Normal",
      notConspicuous: "Not Conspicuous",
      loading: "Loading...",
      minutes: "minutes",
      minutesCap: "Minute",
      startingAt: "starting at",
      steadily: "Steadily > 1,5%",
      importantRemarksDescription:
        "An interpretation of the results and a diagnosis can only be performed by a physician. A comprehensive diagnosis considers not only the breath test but also the medical history and further examinations at the discretion of a practitioner.",
      referenceText:
        "The reference values for the criteria are based on the information and guidelines from the North American Consensus*** on breath tests. In the following, the criteria are briefly explained and supplemented by general notes.",
      point1first: "A breath test with",
      point1second:
        "is suspected SIBO** positive if the following criteria is met:",
      point1textfirst:
        "A rise over lowest preceding value in H₂ production of",
      point1textsecond: "ppm or greater within",
      point1textthird: "minutes after ingesting",
      point2first: "A breath test with",
      point2second:
        "is suspected IMO*** positive if the following criteria is met:",
      point2textfirst:
        "A methane value of at least 10 ppm at some point during the test.",
      point3first:
        "CO₂ is only a control value that checks whether the sample contains sufficient (>1.5%)",
      point3textfirst:
        "CO₂ - breathing air - or whether it has been contaminated with ambient air. Contaminated samples have no significance. This does not mean that the entire test has no significance. However, a retest may be necessary. In almost all cases too low CO₂ values are due to an incorrect sample collection.",
      additionalNoteHeading: "The following additional notes apply:",
      additionalNoteTextfirst: "A rise calculation of up to",
      additionalNoteTextsecond:
        "minutes, inclusively, assumes that the test substrate has not yet reached the colon. The transit time varies from patient to patient. Therefore",
      additionalNotePoint1:
        "The earlier a rise beyond the reference value is observed, the more likely the gas production is located in the small intestine as an indication of SIBO.",
      additionalNotePoint2first: "Elevated (> ",
      additionalNotePoint2second:
        "ppm) basal values (values at 0 minutes) for H₂ may result from a non-compliance with the preparatory diet and rules of conduct and complicate the interpretation of the results. A new test is recommended. Elevated basal values for CH₄ are more common.****",
      SIBO: "Small Intestinal Bacterial Overgrowth",
      IMO: "Intestinal Methane Overgrowth",
      threestars:
        'Rezaie, Ali, et al."Hydrogen and methane-based breath testing in gastrointestinal disorders: the North American Consensus." The American journal of gastroenterology 112.5 (2017): 775. Protocols and Interpretation Help, Hydrogen/Methane Breath Tests. Milwaukee, WI: QuinTron Instrument Company, Inc; 2013.',
      explanation:
        "Explanation of the Rise Calculation using your H₂ Values as an example:",
      explanationfirst: "Your highest hydrogen rise of",
      explanationsecond: "ppm is calculated from the maximum value within",
      explanationthird: "minutes (",
      explanationfourth: "ppm at",
      explanationfifth: "minutes) minus the lowest preceding value of",
      explanationsixth: "ppm. Please note the remarks.",
      missingValuesWarning:
        "Please note that because of one or more missing values, the test results are not completely accurate; however, they may still be informative for a practitioner.",
      footertext: "Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Germany",
      symptoms: {
        bloating: "Bloating",
        abdominalPain: "Abdominal pain",
        diarrhea: "Diarrhea",
        constipation: "Constipation",
        flatulence: "Flatulence",
        mixed: "Mixed",
      },
      testSymptoms: {
        bloating: "Bloating",
        abdominalPain: "Abdominal pain",
        diarrhea: "Diarrhea",
      },
      missingDataFrist: "The tube at minute",
      and: "and",
      missingDataSecond:
        "could not be analyzed because the CO₂ levels were too low.",
      reportNo: "Report No.",
    },

    french: {
      title: "TEST RESPIRATOIRE SIBO",
      generalDetails: "Détails Généraux",
      patientDetails: "Détails du Patient",
      testDetails: "Détails du Test",
      results: "Résultats",
      lastName: "Nom de Famille:",
      firstName: "Prénom:",
      dateOfBirth: "Date de Naissance:",
      dateOfSampleCollection: "Date de Collecte d'Échantillon:",
      dateOfAnalysis: "Date d'Analyse:",
      substratUsed: "Substrat Utilisé:",
      reportSentTo: "Rapport Envoyé À:",
      orderCountry: "Pays de Commande:",
      voluntaryDetails: "Détails Volontaires",
      physicianOrTherapist: "Médecin ou Thérapeute",
      occurenceOfSymptoms: "Apparition des symptômes pendant le test",
      dailySymptoms: "Symptômes Quotidiens",
      siboBreathTest: "Test Respiratoire SIBO**",
      initialTest: "Test Initial",
      followupTest: "Test de Suivi",
      overview:
        "Aperçu des Valeurs d'Hydrogène (H₂) et de Méthane (CH₄) en parties par million (ppm)",
      summary: "Résumé",
      gasRise: "Augmentation des Gaz",
      patientResults: "Accumulation de Gaz",
      referenceValues: "Valeurs de Référence",
      continuePage2: "Continuer à la Page 2",
      importantRemarks: "Remarques Importantes",
      patient: "Patient",
      therapist: "Thérapeute",
      noSymptoms: "Aucun symptôme n'a été signalé pendant le test.",
      symptomsReported:
        "Le patient signale une première occurrence des symptômes suivants pendant le test:",
      tooLow: "Trop Bas",
      normal: "Normal",
      notConspicuous: "Non Significatif",
      loading: "Chargement...",
      minutes: "Minutes",
      minutesCap: "Minute",
      startingAt: "commençant à",
      steadily: "Constamment > 1,5%",
      importantRemarksDescription:
        "L'interprétation des résultats et l'établissement d'un diagnostic ne peuvent être effectués que par un médecin. Un diagnostic complet tient compte non seulement du test respiratoire, mais aussi des antécédents médicaux et d'autres examens à la discrétion du praticien.",
      referenceText:
        "Les valeurs de référence pour les critères sont basées sur les informations et les directives du consensus nord-américain**** sur les tests respiratoires. Dans ce qui suit, les critères sont brièvement expliqués et complétés par des remarques générales.",
      point1first: "Un test respiratoire au",
      point1second:
        "est considéré comme positif pour le SIBO** si les critères suivants sont remplis:",
      point1textfirst: "Une augmentation de",
      point1textsecond:
        "ppm ou plus par rapport à la valeur la plus basse précédente de la production de H₂ dans les ",
      point1textthird: "minutes suivant l'ingestion de",
      point2first: "Un test respiratoire au",
      point2second:
        "est considéré comme positif IMO*** si les critères suivants sont remplis :",
      point2textfirst:
        "Une valeur de méthane d'au moins 10 ppm à n'importe quel moment pendant le test.",
      point3first:
        "Le CO₂ n'est qu'une valeur de contrôle qui vérifie si l'échantillon contient une quantité suffisante (>1,5 %).",
      point3textfirst:
        "CO₂ - air respiré - ou s'il a été contaminé par l'air ambiant. Les échantillons contaminés n'ont aucune valeur. Cela ne signifie pas que l'ensemble du test est sans valeur. Cependant, un nouveau test peut s'avérer nécessaire. Dans presque tous les cas, des valeurs de CO₂ trop faibles sont dues à un prélèvement incorrect de l'échantillon.",
      additionalNoteHeading:
        "Les remarques supplémentaires suivantes s'appliquent :",
      additionalNoteTextfirst: "Un calcul de la montée pouvant aller jusqu'à",
      additionalNoteTextsecond:
        "minutes, inclusivement, suppose que le substrat testé n'a pas encore atteint le côlon. Le temps de transit varie d'un patient à l'autre. Par conséquent, des remarques supplémentaires s'appliquent:",
      additionalNotePoint1:
        "Plus une augmentation supérieure à la valeur de référence est observée tôt, plus il est probable que la production de gaz se situe dans l'intestin grêle, ce qui indique une prolifération bactérienne dans l'intestin grêle (SIBO).",
      additionalNotePoint2first: "Des valeurs basales élevées (> ",
      additionalNotePoint2second:
        "ppm) (valeurs à 0 minute) pour H₂ peuvent résulter d'un non-respect du régime alimentaire préparatoire et des règles de conduite et compliquer l'interprétation des résultats. Un nouveau test est recommandé. Les valeurs basales élevées pour CH₄ sont plus courantes.****",
      SIBO: "Small Intestinal Bacterial Overgrowth. En français: Prolifération bactérienne dans l'intestin grêle",
      IMO: "Intestinal Methane Overgrowth, En français: Surproduction intestinale de méthane",
      threestars:
        'Rezaie, Ali, et al."Hydrogen and methane-based breath testing in gastrointestinal disorders: the North American Consensus." The American journal of gastroenterology 112.5 (2017): 775. Protocols and Interpretation Help, Hydrogen/Methane Breath Tests. Milwaukee, WI: QuinTron Instrument Company, Inc; 2013.',
      explanation:
        "Explication du calcul de la hausse à l'aide de vos valeurs H₂ à titre d'exemple :",
      explanationfirst: "Votre augmentation maximale d'hydrogène de",
      explanationsecond:
        "ppm est calculée à partir de la valeur maximale atteinte en",
      explanationthird: "minutes (",
      explanationfourth: "ppm à",
      explanationfifth: "minutes) moins la valeur minimale précédente de",
      explanationsixth: "ppm. Veuillez noter les remarques.",
      missingValuesWarning:
        "Veuillez noter qu'en raison d'une ou plusieurs valeurs manquantes, les résultats du test ne sont pas tout à fait exacts ; toutefois, ils peuvent tout de même être utiles à votre médecin ou à votre thérapeute.",
      footertext: "Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Allemagne",
      symptoms: {
        bloating: "Ballonement abdominal",
        abdominalPain: "Douleurs abdominales",
        diarrhea: "Diarrhée",
        constipation: "Constipation",
        flatulence: "Flatulences",
        mixed: "Constipation/Diarrhée en alternance",
      },
      testSymptoms: {
        bloating: "Ballonement abdominal",
        abdominalPain: "Douleurs abdominales",
        diarrhea: "Diarrhée",
      },
      missingDataFrist: "Le tube à la minute",
      and: "et",
      missingDataSecond:
        "n'a pas pu être analysé car les niveaux de CO₂ étaient trop bas.",
      reportNo: "Rapport n",
    },

    german: {
      title: " SIBO ATEMGASTEST",
      generalDetails: "Allgemeines Details",
      patientDetails: "Patient*in",
      testDetails: "Zum Test",
      results: "Ergebnisse",
      lastName: "Name",
      firstName: "Vorname",
      dateOfBirth: "Geburtsdatum:",
      dateOfSampleCollection: "Datum der Probenentnahme",
      dateOfAnalysis: "Datum der Analyse",
      substratUsed: "Verwendetes Substrat",
      reportSentTo: "Ergebnis versendet an",
      orderCountry: "Bestellland:",
      voluntaryDetails: "Zusätzliche Angaben",
      physicianOrTherapist: "Behandelnde(r) Ärzt*in bzw. Therapeut*in",
      occurenceOfSymptoms:
        "Auftreten von Symptomen während der Testdurchführung",
      dailySymptoms: "Beschwerden im Alltag",
      siboBreathTest: "SIBO** Atemgastest",
      initialTest: "Ersttest",
      followupTest: "Nachfolgetest",
      overview:
        "Übersicht der Wasserstoff (H₂) und Methan (CH₄) Werte in Teilen pro Million (ppm)",
      summary: "Zusammenfassung",
      gasRise: "Gasanalyse",
      patientResults: "Gasanstieg",
      referenceValues: "Referenzwerte",
      continuePage2: "Weiter zu Seite 2",
      importantRemarks: "Wichtige Bemerkungen",
      patient: "Patient*in",
      therapist: "Therapeut*in",
      noSymptoms: "Während des Tests wurden keine Symptome gemeldet.",
      symptomsReported:
        "Patient*in berichtet über ein erstes Auftreten der folgenden Symptome während des Tests:",
      tooLow: "Zu Niedrig",
      normal: "In Ordnung",
      notConspicuous: "Nicht Auffällig",
      loading: "Laden...",
      minutes: "Minuten",
      minutesCap: "Minute",
      startingAt: "beginnend bei",
      steadily: "konstant > 1,5 %",
      importantRemarksDescription:
        "Der Bericht ist keine Diagnose. Eine Interpretation der Ergebnisse und eine Diagnose können nur durch eine(n) Ärzt*in oder Therapeut*in erfolgen. Zu einer umfassenden Diagnose gehören neben dem Atemgastest eine Anamnese und weitere Untersuchungen nach Ermessen des ärtzlichen bzw. therapeutischen Fachpersonal.",
      referenceText:
        "Die Referenzwerte der Kriterien richten sich nach den Angaben und Richtlinien aus dem North American Consensus**** zum Atemgastest. Im Folgenden werden die Kriterien kurz erläutert und durch allgemeine Hinweise ergänzt.",
      point1first: "Ein Atemgastest mit",
      point1second:
        "unterstützt den Verdacht auf SIBO** wenn folgendes Kriterium erfüllt ist:",
      point1textfirst:
        "Ein Anstieg des niedrigsten vorherigen Wertes der H₂-Produktion um",
      point1textsecond: "ppm oder mehr innerhalb von",
      point1textthird: "Minuten nach Einnahme von",
      point2first: "Ein Atemtest mit",
      point2second:
        "gilt als positiv, wenn die folgenden Kriterien erfüllt sind:",
      point2textfirst:
        "Ein Methangehalt von mindestens 10 ppm zu irgendeinem Zeitpunkt während des Tests.",
      point3first:
        "CO₂ ist lediglich ein Kontrollwert, der überprüft, ob die Probe einen ausreichenden Gehalt (>1,5 %) aufweist.",
      point3textfirst:
        "CO₂ – Atemluft – enthält oder ob sie mit Raumluft vermengt wurde. Stark vermengte Proben haben keine Aussagekraft. Dies bedeutet nicht zwingend, dass der gesamte Test keine Aussagekraft hat. Jedoch ist ein erneuter Test eventuell notwendig. In fast allen Fällen sind zu niedrige CO₂-Werte auf eine inkorrekte Testdurchführung zurückzuführen.",
      additionalNoteHeading: "Zusätzlich gelten folgende Hinweise:",
      additionalNoteTextfirst: "Eine Anstiegsberechnung bis einschließlich ",
      additionalNoteTextsecond:
        "Minuten unterstellt, dass das Testsubstrat den Dickdarm in der Zeit noch nicht erreicht hat. Die Transitzeit unterscheidet sich je nach Patient*in. Demzufolge gilt:",
      additionalNotePoint1:
        "Je früher ein Anstieg über den Referenzwert hinaus beobachtet wird, desto wahrscheinlicher ist es, dass die Gasproduktion im Dünndarm stattfindet, was ein Hinweis auf SIBO darstellt.",
      additionalNotePoint2first: "Erhöhte (> ",
      additionalNotePoint2second:
        "ppm) Basalwerte (Werte bei 0 Minuten)  für H₂ können aus einer Nichteinhaltung der vorbereitenden Diät und Verhaltensregeln resultieren und erschweren die Interpretation der Ergebnisse. Ein erneuter Test ist empfehlenswert. Erhöhte Basalwerte für CH₄ sind üblicher.****",
      SIBO: "Small Intestinal Bacterial Overgrowth. Deutsch: Bakterielle Fehlbesiedlung des Dünndarms.",
      IMO: "Intestinal Methane Overgrowth, Deutsch: methanogene Überbesiedlung des Darms",
      threestars:
        'Rezaie, Ali, et al."Hydrogen and methane-based breath testing in gastrointestinal disorders: the North American Consensus." The American journal of gastroenterology 112.5 (2017): 775. Protocols and Interpretation Help, Hydrogen/Methane Breath Tests. Milwaukee, WI: QuinTron Instrument Company, Inc; 2013.',
      explanation:
        "Erklärung zur Anstiegsberechnung am Beispiel Wasserstoff (H₂):",
      explanationfirst: "Ihr höchster Wasserstoff-Anstieg in Höhe von ",
      explanationsecond: "ppm errechnet sich aus dem Höchstwert innerhalb von ",
      explanationthird: "Minuten (",
      explanationfourth: "ppm bei ",
      explanationfifth:
        "Minuten) abzüglich des niedrigsten vorhergehenden Messwertes in Höhe von ",
      explanationsixth: "ppm. Bitte berücksichtigen Sie die Anmerkungen.",
      missingValuesWarning:
        "Bitte beachten Sie, dass die Testergebnisse aufgrund eines oder mehrerer fehlender Werte nicht vollständig genau sind; aber sie können dennoch für Ihren Ärtz*in oder Therapeut*in aufschlussreich sein.",
      footertext:
        "Sibolab UG, Zehdenicker Str. 7A, 10119 Berlin, Deutschland",
      symptoms: {
        bloating: "Blähbauch",
        abdominalPain: "Abdominale Schmerzen",
        diarrhea: "Diarrhö",
        constipation: "Obstipation",
        flatulence: "Blähungen",
        mixed: "Obstipation/Diarrhö im Wechsel",
      },
      testSymptoms: {
        bloating: "Blähbauch",
        abdominalPain: "Abdominale Schmerzen",
        diarrhea: "Diarrhö",
      },
      missingDataFrist: "Das Röhrchen bei Minute",
      and: "und",
      missingDataSecond:
        "konnte nicht analysiert werden, da die CO₂-Werte zu niedrig waren.",
      reportNo: "Bericht Nr.",
    },
  };

  if (language === "dutch") {
    // Dutch content is not yet localized; use English copy as fallback.
    return translations.english;
  }

  return translations[language] || translations.english;
}

export { getTranslations };
